import { Stack } from '@sureco/design-system'
import { Button, Spin } from '@surecompanies/core_components'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { RouterContextProvider } from 'components/RouterContext'
import useWindowDimensions from 'hooks/useWindowDimensions'
import Page from 'layouts/Page'
import React, { useRef } from 'react'
import { browserHistory } from 'react-router'
import DemoBanner from '../../DemoCustomer/components/DemoBanner'
import DemoCustomerCreateForm, {
  DemoFormRef,
} from '../../DemoCustomer/components/DemoCustomerCreateForm'
import DemoPresetsModal from '../../DemoCustomer/components/DemoPresetsModal'
import { DemoCustomerStep1Data } from '../../DemoCustomer/demoCustomer.schema'
import useEnrollmentConfiguration from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/Enrollment/hooks/useEnrollmentConfiguration'
import useViewDemoCompany from '../../DemoCustomer/hooks/useViewDemoCompany'
import { DEMO_COMPANY_MANAGEMENT, demoCompanyViewPath } from '../../paths'
import useUpdateDemoCompany from './hooks/useUpdateDemoCompany'

interface EditDemoCompanyProps {
  params: { id: string }
}

export default function EditDemoCompany({ params }: EditDemoCompanyProps) {
  const { id } = params

  const { width } = useWindowDimensions()
  const buttonSize = width > 600 ? 'middle' : 'large'
  const formRef = useRef<DemoFormRef>(null)

  // Load existing data to seed the form
  const { data: customerData, isLoading } = useViewDemoCompany({ id })

  // Read-only context only — these values aren't editable here, so the
  // enrollment query never blocks the form from rendering.
  const { getEnrollmentConfigurationQuery } = useEnrollmentConfiguration(id)
  const latestEnrollment = getEnrollmentConfigurationQuery.data?.[0]
  const enrollmentLoading = getEnrollmentConfigurationQuery.isLoading

  const { mutate, isPending } = useUpdateDemoCompany({ id })

  const displayName = (customerData?.name ?? '').replace(/^ZZ - DEMO - /i, '')

  // The sales contact is stored on the customer record as a SURECO contact.
  const salesContact = customerData?.contacts?.find((c) => c.type === 'SURECO')

  const initialConfig = customerData
    ? {
        companyName: displayName,
        salesContact: {
          fullName: salesContact?.fullName ?? '',
          title: salesContact?.title ?? '',
          phone: salesContact?.phone?.number ?? '',
          email: salesContact?.email ?? '',
        },
        // Display-only context — rendered as text beside the name input.
        payrollCycle: customerData.payrollCycle
          ? String(customerData.payrollCycle)
          : undefined,
        benefitEffectiveDate: latestEnrollment?.benefitEffectiveDate,
        enrollmentStartDate: latestEnrollment?.enrollmentStartDate,
        enrollmentEndDate: latestEnrollment?.enrollmentEndDate,
      }
    : undefined

  const handleSubmit = (data: DemoCustomerStep1Data) => {
    mutate(data)
  }

  return (
    <RouterContextProvider>
      <Page
        title={isLoading ? '…' : `Edit ${displayName}`}
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Demo Companies', path: `/${DEMO_COMPANY_MANAGEMENT}` },
              {
                title: isLoading ? '…' : displayName,
                path: `/${demoCompanyViewPath(id)}`,
              },
              { title: 'Edit' },
            ]}
          />
        }
        action={
          <Stack direction="row" space={10}>
            <Button
              type="default"
              size={buttonSize}
              disabled={isPending}
              onClick={() => browserHistory.push(`/${demoCompanyViewPath(id)}`)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size={buttonSize}
              loading={isPending}
              disabled={isLoading}
              onClick={() => formRef.current?.submit()}
            >
              Save Changes
            </Button>
          </Stack>
        }
      >
        {isLoading ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 48 }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <Stack space={24}>
            <DemoBanner description="You are editing a demo company for a sales demonstration." />
            <DemoCustomerCreateForm
              ref={formRef}
              onSubmit={handleSubmit}
              initialConfig={initialConfig}
              enrollmentLoading={enrollmentLoading}
            />
            <DemoPresetsModal />
          </Stack>
        )}
      </Page>
    </RouterContextProvider>
  )
}
